import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import { ItemGrid } from "components/DesignSystem/Accordion/Accordion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import Slider from "components/DesignSystem/Slider/Slider";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { Cross } from "components/icons/Cross";
import { OptionDropdown } from "components/icons/optionDropdown";
import { EditJournalEntry } from "components/JournalEntry/EditJournalEntry";
import { transactionColumn } from "components/Transaction/TransactionsTable";
import {
  ACCOUNTING_METHODS,
  ACCOUNTING_METHODS_OPTIONS,
  ACCOUNTING_METHOD_TYPES,
} from "constants/accountMethod";
import { DISPLAY_DATE } from "constants/date";
import {
  JOURNAL_ENTRY_SOURCES_DISPLAY,
  JOURNAL_ENTRY_STATUS,
  JOURNAL_ENTRY_STATUS_DISPLAY,
  JOURNAL_ENTRY_STATUS_TYPE,
  LT_SERIAL_NUMBER_PREFIX,
} from "constants/journalEntrySources";
import { LEDGER_TYPE_OPTIONS } from "constants/ledgerEntryTypes";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { LockedUi } from "components/LockedUi/LockedUi";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useModal } from "hooks/useModal";
import { useUpdateQuery } from "hooks/useQuery";
import { useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  JournalEntryResult,
  useGetRelatedLedgerEntryQuery,
} from "store/apis/generalLedger";
import { formatDate } from "utils/formatDate";
import { DeleteJournalEntryModal, Invoice } from "./DeleteJournalEntryModal";
import { JournalEntryComments } from "./JournalEntryComments";
import LinkedInvoices from "./LinkedInvoices";
import { PayrollInfo } from "./LinkedPayroll";
import LinkedTransactions from "./LinkedTransactions";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Tab from "components/DesignSystem/Tab/Tab";
import { AuditlogList } from "components/AuditlogList/AuditlogList";
import { JournalEntryTransaction } from "./AddJournalEntryModal";
import ToolTip from "components/design/toolTip";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { MediumInfo } from "components/icons/MediumInfo";
import { useToast } from "hooks/useToast";
import {
  useBulkUpdateJournalEntryStatusMutation,
  useLazyExportJournalEntriesQuery,
} from "store/apis/journalEntry";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { BackendError } from "types/utils/error";
import { openLink } from "utils/openLink";
import { Tag } from "components/DesignSystem/Tag/Tag";

type JournalEntrySliderProps =
  | {
      isOpen: boolean;
      onClose: () => void;
      ledgerEntryId: string;
      transactionId?: never;
    }
  | {
      isOpen: boolean;
      onClose: () => void;
      transactionId: string;
      ledgerEntryId?: never;
    };

const Header = ({
  ledgerEntryId,
  onClose,
}: {
  ledgerEntryId: string;
  onClose: () => void;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { update } = useUpdateQuery();
  const editModal = useModal();
  const deleteModal = useModal();
  const { successToast, alertToast } = useToast();
  const [updateStatus] = useBulkUpdateJournalEntryStatusMutation();
  const [exportJournalEntries] = useLazyExportJournalEntriesQuery();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const { data: relatedLedgerEntry } = useGetRelatedLedgerEntryQuery(
    { groupId, entityId, ledger_entry_id: ledgerEntryId },
    { skip: !groupId || !entityId || !ledgerEntryId }
  );

  const { ledger_entry } = relatedLedgerEntry || {};
  const { entry_name, status } = ledger_entry || {};

  const isActive = status === JOURNAL_ENTRY_STATUS.ACTIVE;

  const closeEditModal = () => {
    editModal.close();
    update({
      query: "edit_journal_entry_id",
      value: null,
    });
  };

  const onDownload = async () => {
    try {
      const { document_id } = await exportJournalEntries({
        groupId,
        entityId,
        ledger_entry_id: ledgerEntryId,
      }).unwrap();

      const { download_url } = await getPreviewUrl({
        groupId,
        fileId: document_id,
      }).unwrap();

      openLink(download_url);
      successToast({ message: "Journal entry downloaded" });
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const handleStatusUpdate = async (status: JOURNAL_ENTRY_STATUS_TYPE) => {
    try {
      await updateStatus({
        groupId,
        entityId,
        ledger_entry_uuids: [ledgerEntryId],
        status,
      }).unwrap();
      successToast({
        message: `Journal entry ${JOURNAL_ENTRY_STATUS_DISPLAY[status]}.`,
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  return (
    <>
      <Slider.Header
        bottom={
          <Tab.List>
            <Tab.Trigger value="DETAILS">Details</Tab.Trigger>
            <Tab.Trigger value="ACTIVITY">Activity</Tab.Trigger>
          </Tab.List>
        }
      >
        <div className="t-flex t-gap-2 t-items-center">
          <Slider.Title>{entry_name}</Slider.Title>
        </div>
        <div className="t-flex t-gap-2 t-items-center">
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <Button size="xsmall" customType="ghost_icon">
                <OptionDropdown />
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content
                sideOffset={8}
                side="bottom"
                className="t-mr-16"
              >
                <LockedUi feature={WRITE_PERMISSION_KEYS.ACCOUNTING}>
                  {isActive ? (
                    <>
                      <Dropdown.Item key="edit" onSelect={editModal.open}>
                        Edit
                      </Dropdown.Item>
                      <Dropdown.Item
                        onSelect={() =>
                          handleStatusUpdate(JOURNAL_ENTRY_STATUS.ARCHIVED)
                        }
                      >
                        Archive
                      </Dropdown.Item>
                    </>
                  ) : (
                    <Dropdown.Item
                      onSelect={() =>
                        handleStatusUpdate(JOURNAL_ENTRY_STATUS.ACTIVE)
                      }
                    >
                      Mark as active
                    </Dropdown.Item>
                  )}
                </LockedUi>
                <Dropdown.Item onSelect={onDownload}>Download</Dropdown.Item>
                <LockedUi feature={WRITE_PERMISSION_KEYS.ACCOUNTING}>
                  <Dropdown.Item
                    key="delete"
                    type="danger"
                    onSelect={deleteModal.open}
                  >
                    Delete
                  </Dropdown.Item>
                </LockedUi>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
          <Button customType="ghost_icon" size="xsmall" onClick={onClose}>
            <Cross color="currentColor" />
          </Button>
        </div>
      </Slider.Header>

      {editModal.isOpen && (
        <EditJournalEntry
          isOpen={editModal.isOpen}
          close={closeEditModal}
          ledgerEntryId={relatedLedgerEntry?.ledger_entry?.uuid!}
        />
      )}
      {deleteModal.isOpen && (
        <DeleteJournalEntryModal
          ledgerEntryId={relatedLedgerEntry?.ledger_entry?.uuid!}
          close={deleteModal.close}
          isOpen={deleteModal.isOpen}
        />
      )}
    </>
  );
};

const Info = ({ ledgerEntryId }: { ledgerEntryId: string }) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { data: relatedLedgerEntry } = useGetRelatedLedgerEntryQuery(
    { groupId, entityId, ledger_entry_id: ledgerEntryId },
    { skip: !groupId || !entityId || !ledgerEntryId }
  );

  const { ledger_entry } = relatedLedgerEntry || {};
  const {
    date,
    created_at,
    basis,
    source,
    ledger_type,
    settings,
    amount,
    status,
  } = ledger_entry || {};

  const isActive = status === JOURNAL_ENTRY_STATUS.ACTIVE;

  // Note: basis field no longer exists in the new structure
  // For migration purposes, we'll handle this differently

  return (
    <div className="t-rounded-lg t-border-neutral-10 t-border t-border-solid">
      <div className="t-p-4 t-flex t-gap-6 t-flex-col">
        <ItemGrid>
          {Boolean(created_at) && (
            <InfoItem label="Created on">
              <>{dayjs(created_at).format(DISPLAY_DATE)}</>
            </InfoItem>
          )}
          {Boolean(date) && (
            <InfoItem label="Entry for">
              <>{dayjs(date).format(DISPLAY_DATE)}</>
            </InfoItem>
          )}

          {source && (
            <InfoItem label="Source">
              {JOURNAL_ENTRY_SOURCES_DISPLAY[source]}
            </InfoItem>
          )}

          <InfoItem label="Journal Entry Type">
            {LEDGER_TYPE_OPTIONS.find((o) => o.value === ledger_type)?.label}
          </InfoItem>

          {basis && (
            <InfoItem label="Basis">
              {ACCOUNTING_METHODS_OPTIONS[basis]}
            </InfoItem>
          )}

          <InfoItem label="Status">
            {isActive ? (
              <Tag icon={false} rounded tagType="green">
                Active
              </Tag>
            ) : (
              <Tag icon={false} rounded tagType="gray">
                Archived
              </Tag>
            )}
          </InfoItem>

          <InfoItem label="Amount">
            <AmountSuperScript amount={Number(amount || 0)} />
          </InfoItem>
        </ItemGrid>

        {settings?.is_recurring_enabled && (
          <div className="t-flex t-gap-3 t-flex-col">
            <div className="t-flex t-items-center t-gap-3">
              <div className="t-text-subtext t-text-text-60">
                Recurrance details
              </div>
              <div className="t-bg-line-gradient t-h-px t-flex-1"></div>
            </div>

            <ItemGrid>
              <InfoItem label="Frequency">
                <span className="t-capitalize">
                  {settings?.frequency?.toLocaleLowerCase()}
                </span>
              </InfoItem>
              {settings.end_date && (
                <InfoItem label="Ends">
                  <span className="t-capitalize">
                    On {formatDate(settings.end_date)}
                  </span>
                </InfoItem>
              )}
            </ItemGrid>
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryAndDescription = ({
  transaction,
  basis,
}: {
  transaction: JournalEntryResult["transactions"][0];
  basis: ACCOUNTING_METHOD_TYPES;
}) => {
  return (
    <div>
      <div className="t-text-body-sm">
        {transaction.transaction.category?.name || "-"}
      </div>
      <p className="t-text-body-sm t-text-text-30 t-m-0">
        {transaction.transaction.description}
      </p>
    </div>
  );
};

const columnHelper =
  createColumnHelper<JournalEntryResult["transactions"][0]>();

const columns = [
  columnHelper.accessor("transaction.ledger_entry_serial_number", {
    size: 5,
    header: () => (
      <div className="t-flex t-items-center t-gap-1.5">
        <span className="!t-pb-0">ID</span>
        <ToolTip
          text={
            <div className="t-text-left t-text-body-sm t-w-60">
              Unique ID allotted to each journal entry’s transaction, retained
              even after deletion for tracking purposes
            </div>
          }
        >
          <div className="t-flex t-items-center">
            <MediumInfo size={13} />
          </div>
        </ToolTip>
      </div>
    ),
    cell: (info) => {
      const { ledger_entry_serial_number } =
        info.row.original.transaction || {};
      return (
        <span className="t-text-text-30">
          {ledger_entry_serial_number || "-"}
        </span>
      );
    },
  }),
  columnHelper.accessor("transaction", {
    header: "Category & Description",
    cell: (info) => {
      // @ts-ignore
      const basis = info.table.options.meta?.basis;
      return (
        <CategoryAndDescription transaction={info.row.original} basis={basis} />
      );
    },
    size: 50,
  }),
  columnHelper.accessor("transaction.logo", {
    header: () => <span className="t-text-center t-block">VENDOR</span>,
    cell: (info) => (
      <span className="t-flex t-items-center t-justify-center">
        {info.row.original.transaction.merchant ? (
          <Avatar
            src={info.getValue() || ""}
            alt={info.row.original.transaction.merchant}
          />
        ) : (
          "-"
        )}
      </span>
    ),
    size: 10,
  }),
  columnHelper.accessor("transaction.classification_details", {
    header: () => <span className="t-text-right t-block">Class</span>,
    cell: (info) => {
      const classificationData = info.getValue();

      const hasClassifications =
        classificationData && classificationData?.length > 0;

      const tooltipContent = hasClassifications && (
        <div className="t-flex t-flex-col t-gap-1">
          {classificationData.map((classification) => (
            <div key={classification.uuid} className="t-flex t-items-center">
              <span className="t-text-neutral-30">{classification.name}</span>:{" "}
              {classification.classes?.[0]?.name || "-"}
            </div>
          ))}
        </div>
      );

      return (
        <div className="t-flex t-justify-center">
          <ConditionalToolTip condition={hasClassifications && tooltipContent}>
            <span className="t-flex t-items-center t-justify-center t-w-6 t-h-6 t-rounded-full t-bg-neutral-0 t-text-caption t-text-neutral-50">
              {hasClassifications ? classificationData.length : 0}
            </span>
          </ConditionalToolTip>
        </div>
      );
    },
    size: 20,
  }),
  columnHelper.accessor("transaction.amount", {
    header: () => <span className="t-text-right t-block">Amount</span>,
    cell: (info) => (
      <div
        className={classNames("t-text-body-sm t-text-right", {
          "t-text-dark_green": Number(info.getValue()) > 0,
        })}
      >
        <AmountSuperScript amount={Math.abs(Number(info.getValue()) || 0)} />
      </div>
    ),
    size: 20,
  }),
  columnHelper.accessor("invoices", {
    header: () => <div className="t-text-center">Invoice</div>,
    cell: (info) => <Invoice invoice={info.getValue()[0]} />,
    size: 15,
  }),
];

const TransactionTable = ({
  transactions,
  basis,
}: {
  transactions: JournalEntryResult["transactions"];
  basis: ACCOUNTING_METHOD_TYPES;
}) => {
  const filteredTransactions = useMemo(() => {
    // Filter transactions based on cash/accrual category structure
    if (basis === "ACCRUAL") {
      return transactions.filter((txn) => txn.accrual_category_id);
    }
    return transactions.filter((txn) => txn.cash_category_id);
  }, [transactions, basis]);

  const table = useReactTable({
    data: filteredTransactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      basis,
    },
  });

  if (filteredTransactions.length === 0) {
    return (
      <div className="t-text-center t-py-8 t-text-text-30">
        No transactions found for {ACCOUNTING_METHODS_OPTIONS[basis]} basis
      </div>
    );
  }

  return (
    <div className="t-overflow-x-auto t-border-neutral-10 t-border-solid t-border t-text-text-60 t-rounded-lg t-overflow-y-hidden t-p-3">
      <TableUI table={table} sticky={false} />
    </div>
  );
};

const JournalEntryBody = ({
  ledgerEntryId,
  onClose,
}: {
  ledgerEntryId: string;
  onClose: () => void;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { update } = useUpdateQuery();

  const {
    data: relatedLedgerEntry,
    isLoading,
    isSuccess,
  } = useGetRelatedLedgerEntryQuery(
    { groupId, entityId, ledger_entry_id: ledgerEntryId },
    { skip: !groupId || !entityId || !ledgerEntryId }
  );

  const {
    transactions = [],
    ledger_entry,
    invoices,
  } = relatedLedgerEntry || {};
  const { linked_payroll_id } = ledger_entry || {};

  // Check for cash and accrual transactions based on new structure
  const hasCashTransactions = transactions.some((t) => t.cash_category_id);
  const hasAccrualTransactions = transactions.some(
    (t) => t.accrual_category_id
  );
  const hasBothCashAndAccrual = hasCashTransactions && hasAccrualTransactions;

  const defaultBasis =
    ledger_entry?.basis === ACCOUNTING_METHODS.CASH_AND_ACCRUAL ||
    !ledger_entry?.basis
      ? ACCOUNTING_METHODS.ACCRUAL
      : ledger_entry?.basis;

  const [basis, setBasis] = useState<ACCOUNTING_METHOD_TYPES | undefined>(
    defaultBasis
  );

  const linkedTransactionColumn = [
    transactionColumn.date({ size: 20 }),
    transactionColumn.from({ size: 10 }),
    transactionColumn.merchant({ size: 30, disabled: true }),
    transactionColumn.amount({ size: 20 }),
  ];

  useEffect(() => setBasis(defaultBasis), [defaultBasis]);

  const editModal = useModal();

  const closeEditModal = () => {
    editModal.close();
    update({
      query: "edit_journal_entry_id",
      value: null,
    });
  };

  return (
    <Async.Root
      {...{ isEmpty: false, isLoading, isSuccess }}
      renderOnLoad={
        <div className="t-flex t-flex-col t-gap-3 t-px-4 t-py-2 t-h-full">
          <Skeleton className="t-h-14" />
          <Skeleton className="t-h-2/6" />
          <Skeleton className="t-h-2/6" />
        </div>
      }
    >
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <Tab.Root defaultValue="DETAILS" className="t-h-full">
          <div className="t-flex t-flex-col t-h-full">
            <DashboardContainer className="t-h-full">
              <DashboardContainer.Header>
                <div className="t-sticky t-top-0 t-z-header t-bg-white">
                  <Header ledgerEntryId={ledgerEntryId} onClose={onClose} />
                </div>
              </DashboardContainer.Header>
              <DashboardContainer.Content>
                <Tab.Content value="DETAILS">
                  <div className="t-flex t-p-4 t-gap-4 t-flex-col">
                    <Info ledgerEntryId={ledgerEntryId} />
                    {linked_payroll_id && (
                      <PayrollInfo
                        entityId={entityId}
                        payrollId={linked_payroll_id}
                      />
                    )}
                    {ledger_entry && (
                      <LinkedTransactions
                        ledgerEntryId={ledger_entry.uuid}
                        entityId={entityId}
                        columns={linkedTransactionColumn}
                      />
                    )}
                    {ledger_entry && (
                      <LinkedInvoices
                        ledgerEntryId={ledger_entry.uuid}
                        entityId={entityId}
                      />
                    )}

                    <div className="t-flex t-flex-col t-gap-3">
                      <div className="t-flex t-items-center t-justify-between">
                        <div className="t-text-subtext">Line Items</div>
                        <div className="t-flex t-items-center t-gap-3">
                          {hasBothCashAndAccrual && (
                            <ToggleGroup
                              value={basis}
                              onValueChange={(t: ACCOUNTING_METHOD_TYPES) =>
                                setBasis(t)
                              }
                            >
                              <ToggleGroupItem
                                value={ACCOUNTING_METHODS.ACCRUAL}
                              >
                                Accrual
                              </ToggleGroupItem>
                              <ToggleGroupItem value={ACCOUNTING_METHODS.CASH}>
                                Cash
                              </ToggleGroupItem>
                            </ToggleGroup>
                          )}
                        </div>
                      </div>
                      {basis && (
                        <TransactionTable
                          transactions={transactions}
                          basis={basis}
                        />
                      )}
                    </div>

                    <JournalEntryComments ledgerEntryId={ledgerEntryId} />
                  </div>
                  {editModal.isOpen && (
                    <EditJournalEntry
                      isOpen={editModal.isOpen}
                      close={closeEditModal}
                      ledgerEntryId={relatedLedgerEntry?.ledger_entry?.uuid!}
                    />
                  )}
                </Tab.Content>
                <Tab.Content value="ACTIVITY" className="t-h-full" asChild>
                  <div className="t-py-4">
                    <AuditlogList
                      entityId={entityId}
                      objectId={ledgerEntryId}
                    />
                  </div>
                </Tab.Content>
              </DashboardContainer.Content>
            </DashboardContainer>
          </div>
        </Tab.Root>
      </Async.Success>
    </Async.Root>
  );
};

export const JournalEntrySlider = ({
  isOpen,
  onClose,
  ledgerEntryId,
  transactionId,
}: JournalEntrySliderProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { data: relatedLedgerEntry, isLoading } = useGetRelatedLedgerEntryQuery(
    { groupId, entityId, transactionId },
    { skip: !groupId || !entityId || !transactionId || !isOpen }
  );
  const entryId = ledgerEntryId || relatedLedgerEntry?.ledger_entry.uuid;

  return (
    <Slider.Root open={isOpen} onOpenChange={onClose}>
      <Slider.Content
        open={isOpen}
        useCustomOverlay
        className="t-h-full"
        width={800}
      >
        {isLoading ? (
          <div className="t-flex t-flex-col t-gap-3 t-px-4 t-py-2 t-h-full">
            <Skeleton className="t-h-14" />
            <Skeleton className="t-h-2/6" />
            <Skeleton className="t-h-2/6" />
          </div>
        ) : (
          <>
            {entryId && (
              <JournalEntryBody ledgerEntryId={entryId} onClose={onClose} />
            )}
          </>
        )}
      </Slider.Content>
    </Slider.Root>
  );
};
